// needs to support both client and server because of post grid (client) and post slider (server)
import { customSanitize } from "../../utils/customSanitize";
import { optimizeURL } from "../../utils/url";
import styles from "./PostCard.module.scss";
import { ImageType } from "../../types/baseTypes";
import { WP_Term } from "wp-types";
import { ContentTag } from "../ContentTag/ContentTag";
import { MediaFormatType } from "@/types/ModuleOptionsType";
import { Button } from "../Button/Button";
import { MediaItem } from "../MediaItem/MediaItem";
import { CustomLink } from "../CustomLink";
import { CategoryOverviewPages } from "@/getData/getPostGridData";
import type { GetLabel } from "@/getData/fetchData.server";
import { Breakpoint } from "@aleksundshantu/aunds-utils-react/layout";
import CardLinkOverlay from "@/components/CardLinkOverlay/CardLinkOverlay.tsx";

export type PostsGridElementType = {
  id: number;
  date: string;
  title: string;
  link: string;
  image: ImageType | false;
  excerpt: string;
  categories: WP_Term[];
  media_format?: MediaFormatType;
};

interface PostCardProps {
  element: PostsGridElementType;
  border_radius: boolean;
  box_shadow: boolean;
  showBox: boolean;
  media_format?: MediaFormatType;
  skeleton?: boolean;
  allCategories?: CategoryOverviewPages[];
  getLabel: GetLabel;
  columnNumbers: Record<Breakpoint, number>;
}

const PostCard = ({
  element,
  border_radius,
  box_shadow,
  showBox,
  columnNumbers,
  media_format,
  skeleton,
  allCategories,
  getLabel,
}: PostCardProps) => {
  const { categories, excerpt, image, link, title } = element;
  const filteredCategories = categories.filter(
    (el) => el.name !== "Unkategorisiert",
  );

  // Todo: Remove this when postsslider have media_format set
  const defaultMediaFormat: MediaFormatType = {
    mobile: "square",
    tablet: "square",
    desktop: "square",
    fullhd: "square",
  };

  const columnNumberClasses = columnNumbers
    ? typeof columnNumbers === "string"
      ? styles["columns-" + columnNumbers + "-mobile"]
      : Object.entries(columnNumbers).reduce((prev, [breakpoint, value]) => {
          return `${prev} ${styles["columns-" + value + "-" + breakpoint]}`;
        }, "")
    : "";

  const optimizedURL = optimizeURL(link);
  const skeletonElementClass = skeleton ? "skeleton-element" : "";
  return (
    <article
      className={`${styles["embla-slide"]} ${styles["slider-element"]} ${columnNumberClasses} ${box_shadow && showBox ? "has-box-shadow" : ""} ${border_radius && showBox ? "has-border-radius" : ""} ${showBox ? "is-box" : ""} is-flex ${styles[image ? "has-image" : "no-image"]}`}
    >
      <CardLinkOverlay title={title} link={link} getLabel={getLabel} />
      <section
        className={`${styles["info-wrapper"]} is-flex is-flex-direction-column is-justify-content-center ${showBox ? "is-box" : ""}`}
      >
        <header>
          <p className={styles["info-top"] + " pb-4"}>
            {filteredCategories.length > 0 &&
              filteredCategories.map((category, index) => {
                return (
                  <CustomLink
                    className={`${styles["disable-hover"]} ${styles["category-link"]}`}
                    key={index}
                    href={String(
                      (allCategories || []).find(
                        (el) => el.slug === category.slug,
                      )?.link,
                    )}
                    target="_self"
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: customSanitize(category.name),
                      }}
                      className={`category mr-2 ${skeletonElementClass}`}
                    />
                  </CustomLink>
                );
              })}
          </p>
          {title && (
            <p
              role="heading"
              aria-level={3}
              className={`${styles.title} title pb-0 mb-4 ${skeletonElementClass}`}
              dangerouslySetInnerHTML={{
                __html: customSanitize(title),
              }}
            />
          )}
          {excerpt && (
            <ContentTag
              content={excerpt}
              extraClass={`${styles["content"]} pb-0 mb-4 ${skeletonElementClass}`}
            />
          )}
        </header>

        {link && (
          <footer>
            <Button
              type="secondary"
              href={optimizedURL!}
              target="_self"
              className={`my-0 pb-0 ${skeletonElementClass}`}
              aria-label={getLabel("aria_label_postlink").replace(
                "$posttitle",
                title,
              )}
              disablePrefetch // Disable prefetching for grid links https://trello.com/c/g5k075FS
            >
              {getLabel("read_more_label")}
            </Button>
          </footer>
        )}
      </section>
      {image && (
        <MediaItem
          hasOverlay={false}
          image_desktop={image}
          className={`${skeletonElementClass}`}
          mediaFormat={media_format ? media_format : defaultMediaFormat}
          sizes="(min-width: 768px) 33vw, 100vw"
          linkProps={{ tabIndex: -1 }}
          disablePrefetch // Disable prefetching for grid links https://trello.com/c/g5k075FS
        />
      )}
    </article>
  );
};

export default PostCard;
